import React, { useState } from 'react';

// Column widths (must match the header in App.js):
//   # (40px) | Title+Artist (flex-1) | Key (64px) | BPM (72px) | Notes (88px) | Actions (120px)

function keyBadgeClass(key) {
  if (!key) return '';
  return 'bg-blue-500/25 text-blue-300';
}

export default function SongRow({ song, index, isSelected, onOpen, onToggleSetlist, canEdit, onEdit, onDelete, setlistPosition = null }) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsAnimating(true);
    onToggleSetlist();
    // Reset after the bounce completes so it can retrigger on next click
    setTimeout(() => setIsAnimating(false), 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen("sheet_music");
    }
  };

  return (
    <div
      role="row"
      data-song-row="true"
      tabIndex={0}
      onClick={() => onOpen()}
      onKeyDown={handleKeyDown}
      className={[
        'flex w-full min-h-[64px] items-center border-l-2',
        'cursor-pointer select-none transition-colors duration-[150ms] ease-out',
        'active:opacity-90',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue/50',
        isSelected
          ? 'border-l-accent-green bg-accent-green/5 hover:bg-accent-green/10'
          : 'border-l-transparent hover:bg-elevated',
      ].join(' ')}
    >
      {/* # */}
      <div role="cell" className="hidden sm:flex w-[40px] shrink-0 items-center justify-center">
        <span className="font-mono text-xs text-text-muted">{index + 1}</span>
      </div>

      {/* Title + Artist */}
      <div role="cell" className="min-w-0 flex-1 py-3 pr-4">
        <div className="flex items-center gap-2 min-w-0">
          <p className="truncate text-[15px] font-semibold leading-snug text-text-primary">{song.title}</p>
          {setlistPosition !== null && (
            <span
              className="shrink-0 rounded bg-accent-green/20 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-accent-green"
              aria-label={`Song ${setlistPosition} in setlist`}
            >
              #{setlistPosition}
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-text-secondary">{song.artist}</p>
        {/* Mobile-only metadata row — hidden on sm+ where columns show instead */}
        <div className="mt-1 flex items-center gap-1.5 sm:hidden">
          {song.key && (
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${keyBadgeClass(song.key)}`}>
              {song.key}
            </span>
          )}
          {song.bpm && (
            <span className="rounded-full bg-white/[0.10] px-2 py-0.5 text-[11px] text-text-secondary">
              {song.bpm} BPM
            </span>
          )}
          {song.noteCount > 0 && (
            <span
              onClick={(e) => { e.stopPropagation(); onOpen("notes"); }}
              className="cursor-pointer rounded-full bg-purple-500/25 px-2 py-0.5
                         text-[11px] font-medium text-purple-300"
            >
              {song.noteCount} note{song.noteCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Key badge — colour-coded by key family */}
      <div role="cell" className="hidden sm:flex w-[64px] shrink-0 items-center">
        {song.key && (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${keyBadgeClass(song.key)}`}>
            {song.key}
          </span>
        )}
      </div>

      {/* BPM badge */}
      <div role="cell" className="hidden sm:flex w-[72px] shrink-0 items-center">
        {song.bpm && (
          <span className="rounded-full bg-white/[0.10] px-2.5 py-1 text-xs text-text-secondary">
            {song.bpm} BPM
          </span>
        )}
      </div>

      {/* Notes badge */}
      <div role="cell" className="hidden sm:flex w-[88px] shrink-0 items-center">
        {song.noteCount > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); onOpen("notes"); }}
            className="cursor-pointer rounded-full bg-purple-500/25 px-2.5 py-1 text-xs font-medium text-purple-300 hover:bg-purple-500/40"
          >
            {song.noteCount}
          </span>
        )}
      </div>

      {/* Actions */}
      <div role="cell" className="flex w-[64px] sm:w-[152px] shrink-0 items-center justify-end gap-1 sm:gap-2 pr-2 sm:pr-3">
        {/* Edit (pencil) — admin only */}
        {canEdit && (
          <button
            type="button"
            title="Edit song"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="hidden sm:flex rounded p-2 text-text-muted hover:text-text-primary"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5z" />
            </svg>
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            title="Delete song"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="hidden sm:flex rounded p-2 text-text-muted hover:text-danger"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="2 4 14 4" />
              <path d="M6 4V2h4v2" />
              <path d="M3 4l1 9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-9" />
              <line x1="6.5" y1="7" x2="6.5" y2="11" />
              <line x1="9.5" y1="7" x2="9.5" y2="11" />
            </svg>
          </button>
        )}

        {/* Add/Added — scale-95 bounce on click via isAnimating state */}
        <button
          data-setlist-add="true"
          onClick={handleToggle}
          className={[
            'flex items-center gap-1 rounded px-3 py-1.5 text-xs font-medium',
            'transition-all duration-150',
            isAnimating ? 'scale-95' : 'scale-100',
            isSelected
              ? 'border border-accent-green/40 bg-accent-green/15 text-accent-green'
              : 'border border-accent/50 text-accent hover:bg-accent hover:text-white',
          ].join(' ')}
        >
          {isSelected ? (
            <>
              <svg
                className="h-3 w-3 shrink-0"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M2 6.5l3 3 5-5" />
              </svg>
              Added
            </>
          ) : (
            'Add'
          )}
        </button>
      </div>
    </div>
  );
}
